import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Icon } from '@iconify/react'
import { get, patch } from '../../../../utils/Api'
import { useGlobalContext } from '../../../../utils/Context'
import FormLoader from '../../../../components/loader/FormLoader'
import Pagination from '../../../../components/pagination/Pagination'
import './Escalations.scss'

type EscalationStatus = 'pending' | 'in_progress' | 'resolved' | 'cancelled'
type Priority = 'low' | 'medium' | 'high' | 'urgent'

interface EscalationSummary {
  _id: string
  referenceId: string
  status: EscalationStatus
  priority: Priority
  reason: string
  userId: {
    _id: string
    firstname: string
    lastname: string
    email: string
  } | null
  userSnapshot: { firstname: string; lastname: string; email: string }
  assignedTo: { _id: string; email: string } | null
  createdAt: string
}

interface Stats {
  byStatus: {
    pending: number
    in_progress: number
    resolved: number
    cancelled: number
  }
  pendingByPriority: {
    urgent: number
    high: number
    medium: number
    low: number
  }
  last24Hours: number
}

type TabKey = 'all' | 'my-queue' | 'pending' | 'in_progress'

const TABS: { key: TabKey; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'my-queue', label: 'My Queue' },
  { key: 'pending', label: 'Pending' },
  { key: 'in_progress', label: 'In Progress' },
]

function PriorityBadge({ priority }: { priority: Priority }) {
  const labels: Record<Priority, string> = {
    urgent: 'Urgent',
    high: 'High',
    medium: 'Medium',
    low: 'Low',
  }
  return (
    <span className={`badge priority-${priority}`}>{labels[priority]}</span>
  )
}

function StatusBadge({ status }: { status: EscalationStatus }) {
  const icons: Record<EscalationStatus, string> = {
    pending: '⏳',
    in_progress: '🔄',
    resolved: '✅',
    cancelled: '❌',
  }
  const labels: Record<EscalationStatus, string> = {
    pending: 'Pending',
    in_progress: 'In Progress',
    resolved: 'Resolved',
    cancelled: 'Cancelled',
  }
  return (
    <span className={`badge status-${status}`}>
      {icons[status]} {labels[status]}
    </span>
  )
}

function timeAgo(dateStr: string) {
  const diff = Date.now() - new Date(dateStr).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  if (days === 1) return 'Yesterday'
  return `${days}d ago`
}

function Escalations() {
  const navigate = useNavigate()
  const location = useLocation()
  const { notify } = useGlobalContext()

  const tabFromUrl =
    (new URLSearchParams(location.search).get('tab') as TabKey) || 'all'

  const [stats, setStats] = useState<Stats | null>(null)
  const [escalations, setEscalations] = useState<EscalationSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [statsLoading, setStatsLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<TabKey>(tabFromUrl)
  const [page, setPage] = useState(1)
  const [totalDocs, setTotalDocs] = useState(0)
  const [actionLoading, setActionLoading] = useState<string | null>(null)

  const [filters, setFilters] = useState({
    status: '',
    priority: '',
    search: '',
  })
  const [searchInput, setSearchInput] = useState('')

  // Fetch stats
  function fetchStats() {
    setStatsLoading(true)
    get('admin/escalations/stats')
      .then((res: any) => {
        if (res.success) setStats(res.data)
        setStatsLoading(false)
      })
      .catch(() => setStatsLoading(false))
  }

  // Fetch list
  function fetchEscalations(pageNum: number) {
    setLoading(true)
    let endpoint = ''

    if (activeTab === 'my-queue') {
      endpoint = `admin/escalations/my-assigned?page=${pageNum}&limit=20`
      if (filters.status) endpoint += `&status=${filters.status}`
    } else {
      endpoint = `admin/escalations?page=${pageNum}&limit=20&sortBy=createdAt&sortOrder=desc`
      if (activeTab === 'pending') endpoint += `&status=pending`
      else if (activeTab === 'in_progress') endpoint += `&status=in_progress`
      else if (filters.status) endpoint += `&status=${filters.status}`
      if (filters.priority) endpoint += `&priority=${filters.priority}`
    }

    get(endpoint)
      .then((res: any) => {
        if (res.success) {
          setEscalations(res.data.escalations || [])
          setTotalDocs(res.data.pagination?.totalDocs || 0)
        } else {
          notify('error', res.message || 'Failed to load escalations')
        }
        setLoading(false)
      })
      .catch(() => {
        setLoading(false)
        notify('error', 'Failed to load escalations')
      })
  }

  useEffect(() => {
    fetchStats()
  }, [])

  useEffect(() => {
    setPage(1)
    fetchEscalations(1)
  }, [activeTab, filters])

  const handleSearch = () => {
    setFilters((prev) => ({ ...prev, search: searchInput }))
  }

  const handleFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFilters((prev) => ({ ...prev, [e.target.name]: e.target.value }))
  }

  const handlePageChange = (newPage: number) => {
    setPage(newPage)
    fetchEscalations(newPage)
  }

  const assignToMe = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation()
    setActionLoading(id)
    try {
      const res = await patch(`admin/escalations/${id}/assign`, {})
      if (res.success) {
        notify('success', 'Assigned to you')
        fetchEscalations(page)
      } else {
        notify('error', res.message || 'Failed to assign')
      }
    } catch {
      notify('error', 'Something went wrong')
    }
    setActionLoading(null)
  }

  const markInProgress = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation()
    setActionLoading(id)
    try {
      const res = await patch(`admin/escalations/${id}/status`, {
        status: 'in_progress',
      })
      if (res.success) {
        notify('success', 'Marked as In Progress')
        fetchEscalations(page)
      } else {
        notify('error', res.message || 'Failed to update status')
      }
    } catch {
      notify('error', 'Something went wrong')
    }
    setActionLoading(null)
  }

  const getUserName = (esc: EscalationSummary) => {
    if (esc.userId) return `${esc.userId.firstname} ${esc.userId.lastname}`
    return `${esc.userSnapshot.firstname} ${esc.userSnapshot.lastname}`
  }

  return (
    <div className='escalations-page'>
      {/* Page header */}
      <div className='page-header'>
        <div
          className='title-row flex justify-between items-center flex-wrap'
          style={{ gap: '12px' }}
        >
          <h2></h2>
          <div className='header-tabs'>
            {TABS.map((tab) => (
              <button
                key={tab.key}
                className={activeTab === tab.key ? 'active' : ''}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Stats */}
        <div className='stats-row'>
          <div className='stat-card pending'>
            <p>⏳ Pending</p>
            <h3>{statsLoading ? '…' : (stats?.byStatus.pending ?? 0)}</h3>
          </div>
          <div className='stat-card in-progress'>
            <p>🔄 In Progress</p>
            <h3>{statsLoading ? '…' : (stats?.byStatus.in_progress ?? 0)}</h3>
          </div>
          <div className='stat-card resolved'>
            <p>✅ Resolved</p>
            <h3>{statsLoading ? '…' : (stats?.byStatus.resolved ?? 0)}</h3>
          </div>
          <div className='stat-card cancelled'>
            <p>❌ Cancelled</p>
            <h3>{statsLoading ? '…' : (stats?.byStatus.cancelled ?? 0)}</h3>
          </div>
          <div className='stat-card today'>
            <p>📅 Last 24h</p>
            <h3>{statsLoading ? '…' : (stats?.last24Hours ?? 0)}</h3>
          </div>
        </div>

        {/* Filters */}
        <div className='filter-row'>
          {activeTab !== 'pending' && activeTab !== 'in_progress' && (
            <select
              name='status'
              value={filters.status}
              onChange={handleFilterChange}
            >
              <option value=''>All Status</option>
              <option value='pending'>Pending</option>
              <option value='in_progress'>In Progress</option>
              <option value='resolved'>Resolved</option>
              <option value='cancelled'>Cancelled</option>
            </select>
          )}
          {activeTab !== 'my-queue' && (
            <select
              name='priority'
              value={filters.priority}
              onChange={handleFilterChange}
            >
              <option value=''>All Priority</option>
              <option value='urgent'>Urgent</option>
              <option value='high'>High</option>
              <option value='medium'>Medium</option>
              <option value='low'>Low</option>
            </select>
          )}
          <div className='search-wrap'>
            <input
              type='search'
              placeholder='Search...'
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
            <button onClick={handleSearch}>Search</button>
          </div>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <FormLoader position='center' />
      ) : escalations.length === 0 ? (
        <div className='empty-state'>
          <Icon icon='mdi:headset-off' />
          <p>No escalations found</p>
        </div>
      ) : (
        <>
          <div className='table-wrap desktop-table'>
            <table>
              <thead>
                <tr>
                  <th>Reference</th>
                  <th>User</th>
                  <th>Priority</th>
                  <th>Status</th>
                  <th>Assigned To</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {escalations.map((esc) => (
                  <tr
                    key={esc._id}
                    onClick={() => navigate(`/admin/escalation/${esc._id}`)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td>
                      <span className='SI'>{esc.referenceId}</span>
                    </td>
                    <td>{getUserName(esc)}</td>
                    <td>
                      <PriorityBadge priority={esc.priority} />
                    </td>
                    <td>
                      <StatusBadge status={esc.status} />
                    </td>
                    <td>
                      {esc.assignedTo ? (
                        `${esc.assignedTo.email}`
                      ) : (
                        <span style={{ color: '#aaa', fontStyle: 'italic' }}>
                          Unassigned
                        </span>
                      )}
                    </td>
                    <td>{timeAgo(esc.createdAt)}</td>
                    <td className='icons' onClick={(e) => e.stopPropagation()}>
                      {esc.status === 'pending' && (
                        <>
                          <span title='Assign to me'>
                            <Icon
                              icon='mdi:account-check'
                              className='put'
                              onClick={(e) => assignToMe(e, esc._id)}
                              style={{
                                opacity: actionLoading === esc._id ? 0.5 : 1,
                              }}
                            />
                          </span>
                          <span title='Start progress'>
                            <Icon
                              icon='mdi:play-circle-outline'
                              className='put'
                              onClick={(e) => markInProgress(e, esc._id)}
                              style={{
                                opacity: actionLoading === esc._id ? 0.5 : 1,
                              }}
                            />
                          </span>
                        </>
                      )}
                      <span title='View'>
                        <Icon
                          icon='mdi:eye-outline'
                          className='put'
                          onClick={() =>
                            navigate(`/admin/escalation/${esc._id}`)
                          }
                        />
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className='mobile-cards'>
            {escalations.map((esc) => (
              <div
                key={esc._id}
                className='esc-card'
                onClick={() => navigate(`/admin/escalation/${esc._id}`)}
              >
                <div className='card-top'>
                  <span className='ref'>{esc.referenceId}</span>
                  <PriorityBadge priority={esc.priority} />
                </div>
                <p style={{ fontSize: '13px', marginBottom: '6px' }}>
                  {getUserName(esc)}
                </p>
                <div className='card-bottom'>
                  <StatusBadge status={esc.status} />
                  <p>{timeAgo(esc.createdAt)}</p>
                </div>
              </div>
            ))}
          </div>

          <div className='pagination-wrap'>
            <Pagination
              currentPage={page}
              totalCount={totalDocs}
              pageSize={20}
              onPageChange={(newPage: number) => handlePageChange(newPage)}
            />
          </div>
        </>
      )}
    </div>
  )
}

export default Escalations
